import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react';
import {
  getAuthStorage,
  isTauriEnvironment,
  type ClearAuthOptions,
} from '@daysheetly/desktop-bridge';
import { AUTH_LOGOUT_EVENT } from '@/relay/fetchGraphQL';
import { getActivityBridge } from '@/features/activityAssistant/activityAssistant.service';
import {
  setRuntimeActiveWorkspaceId,
  setRuntimeAuthSession,
} from '@/features/auth/authRuntime';
import { identifyUser, resetAnalytics } from '@/analytics/posthog';

export interface AuthUser {
  id: string;
  name: string;
  email: string;
}

interface AuthContextValue {
  token: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  isHydrating: boolean;
  activeWorkspaceId: string | null;
  setActiveWorkspaceId: (id: string | null) => void;
  login: (token: string, user: AuthUser) => Promise<void>;
  logout: (options?: ClearAuthOptions) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const STORAGE_KEY = 'daysheetly_token';
const USER_KEY = 'daysheetly_user';
const ACTIVE_WORKSPACE_KEY = 'daysheetly_active_workspace_id';

function readLegacyWebUser(): AuthUser | null {
  const stored = localStorage.getItem(USER_KEY);
  if (!stored) return null;
  try {
    const parsed = JSON.parse(stored) as Partial<AuthUser>;
    if (!parsed.name || !parsed.email) return null;
    // Sessions created before this feature did not persist the user ID. No
    // activity exists for them yet, so the email is a safe temporary key
    // until the next login refreshes the canonical ID.
    return { id: parsed.id || parsed.email, name: parsed.name, email: parsed.email };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const native = isTauriEnvironment();
  const [authStorage] = useState(() => getAuthStorage());
  const [token, setToken] = useState<string | null>(
    () => (native ? null : localStorage.getItem(STORAGE_KEY)),
  );
  const [user, setUser] = useState<AuthUser | null>(
    () => (native ? null : readLegacyWebUser()),
  );
  const [activeWorkspaceId, setActiveWorkspaceIdState] = useState<
    string | null
  >(() => (native ? null : localStorage.getItem(ACTIVE_WORKSPACE_KEY)));
  const [isHydrating, setIsHydrating] = useState(native);

  useEffect(() => {
    if (!native) {
      setRuntimeAuthSession({ token, activeWorkspaceId });
      return;
    }

    // Remove credentials left by pre-AuthStorage desktop builds. The secure
    // OS credential store is the only persisted source in Tauri.
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(ACTIVE_WORKSPACE_KEY);

    let cancelled = false;
    authStorage
      .hydrate()
      .then(async (session) => {
        if (cancelled) return;
        setToken(session?.token ?? null);
        setUser(session?.user ?? null);
        setActiveWorkspaceIdState(session?.activeWorkspaceId ?? null);
        setRuntimeAuthSession({
          token: session?.token ?? null,
          activeWorkspaceId: session?.activeWorkspaceId ?? null,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setToken(null);
        setUser(null);
        setActiveWorkspaceIdState(null);
        setRuntimeAuthSession({ token: null, activeWorkspaceId: null });
      })
      .finally(() => {
        if (!cancelled) setIsHydrating(false);
      });
    return () => {
      cancelled = true;
    };
    // The storage implementation is selected once for the lifetime of the
    // provider. Re-running hydration after each state change would overwrite
    // a fresh login with stale secure-store data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setActiveWorkspaceId = useCallback((id: string | null) => {
    if (!native) {
      if (id === null) {
        localStorage.removeItem(ACTIVE_WORKSPACE_KEY);
      } else {
        localStorage.setItem(ACTIVE_WORKSPACE_KEY, id);
      }
    }
    setRuntimeActiveWorkspaceId(id);
    setActiveWorkspaceIdState(id);
    void authStorage.setActiveWorkspaceId(id).catch(() => {
      // The in-memory selection remains usable for this session. A native
      // secure-store error is surfaced again on the next auth operation.
    });
  }, [authStorage, native]);

  const login = useCallback(
    async (newToken: string, newUser: AuthUser) => {
      await authStorage.save({
        token: newToken,
        user: newUser,
        activeWorkspaceId,
      });
      setRuntimeAuthSession({ token: newToken, activeWorkspaceId });
      setToken(newToken);
      setUser(newUser);
      identifyUser(newUser.id);
      // Native auth commands bind the local database partition atomically.
      // Browser-injected bridges used by E2E tests do not have that command.
      if (!native) await getActivityBridge().setActiveUser(newUser.id);
    },
    [activeWorkspaceId, authStorage, native],
  );

  const logout = useCallback(
    async (options?: ClearAuthOptions) => {
      let failure: unknown;
      try {
        await authStorage.clear(options);
        // See login(): native clear_auth_session also unbinds the partition.
        if (!native) await getActivityBridge().setActiveUser(null);
      } catch (error) {
        failure = error;
      } finally {
        setRuntimeAuthSession({ token: null, activeWorkspaceId: null });
        setToken(null);
        setUser(null);
        setActiveWorkspaceIdState(null);
        resetAnalytics();
      }
      if (failure) throw failure;
    },
    [authStorage, native],
  );

  // Listen for "your token is stale" signals from the GraphQL network layer
  // (see fetchGraphQL.ts). When the server reports a 401, a `Not
  // authenticated` error, or returns `viewer: null` while we still have a
  // token, we wipe local storage so `<ProtectedRoute>` redirects to
  // `/login` instead of leaving the user stuck on a half-rendered page.
  useEffect(() => {
    const handler = () => void logout();
    window.addEventListener(AUTH_LOGOUT_EVENT, handler);
    return () => window.removeEventListener(AUTH_LOGOUT_EVENT, handler);
  }, [logout]);

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        isAuthenticated: !!token,
        isHydrating,
        activeWorkspaceId,
        setActiveWorkspaceId,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export { STORAGE_KEY, USER_KEY, ACTIVE_WORKSPACE_KEY };
